import { toolsApi, toolRunsApi } from '@/lib/api';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    const done = () => {
      signal?.removeEventListener('abort', abort);
      resolve();
    };
    const timer = setTimeout(done, ms);
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(new DOMException('Image generation cancelled', 'AbortError'));
    };
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}

function firstImageFile(run) {
  return (run?.resultFiles || []).find(file =>
    String(file?.mimeType || '').startsWith('image/') || file?.fileType === 'image');
}

/**
 * Runs the shared `image-generator` tool (same credit path as Tools → Image
 * Generator). Pass `modelId` to pick a specific image model; omit it to use
 * the tool default.
 */
export async function generateEditorImage({
  prompt,
  style = 'photorealistic',
  aspectRatio = '16:9',
  modelId = null,
  signal,
  onStatus,
}) {
  const accepted = await toolsApi.run('image-generator', {
    inputs: {
      prompt: String(prompt || '').trim(),
      style,
      aspectRatio,
      negativePrompt: '',
    },
    modelId: modelId || undefined,
  });
  const runId = accepted?.toolRun?.id || accepted?.toolRun?._id;
  if (!runId) throw new Error('Image generator returned no run id');

  for (let attempt = 0; attempt < 90; attempt += 1) {
    if (signal?.aborted) throw new DOMException('Image generation cancelled', 'AbortError');
    const response = await toolRunsApi.poll(runId);
    const run = response?.toolRun || response;
    onStatus?.(run?.status || 'running');
    if (TERMINAL_STATUSES.has(run?.status)) {
      if (run.status !== 'completed') {
        throw new Error(run?.error?.message || 'Image generation failed');
      }
      const file = firstImageFile(run);
      if (!file?.cdnPath) throw new Error('Image generation completed without an image');
      return file;
    }
    await wait(1500, signal);
  }
  throw new Error('Image generation timed out');
}
